import { Pipe, PipeTransform } from '@angular/core';
import { reduceWhile } from 'ramda';

interface FileSize {
  size: number;
  unit: string;
}

const BYTES = 'bytes';
const UNITS = [
  'KB',
  'MB',
  'GB'
];

const greaterThan1000 = ({size}: FileSize) => size > 1000;
const divideBy1000 = ({size}: FileSize, unit: string): FileSize => ({
  size: size / 1000,
  unit
});

const formatNumber = (num: number, precision: number): string =>
  num.toFixed(precision);

const formatFileSize = ({size, unit}: FileSize, precision: number): string =>
  `${formatNumber(size, precision)} ${unit}`;


/**
 * Use this pipe to format number that represents the size of a file in bytes to KB, MB or GB.
 * If we have 1,048,576 bytes that will be formatted as 1MB. 2,048 bytes will be formatted as 2KB. 775650 bytes will be
 * formatted as 775.6KB. By default precision of 1 is used for the decimal digits. If you want more decimal digits you
 * should pass the number of decimal digits as a parameter to the pipe.
 *
 * Example usage:
 * {{value | fileSize: 2}}
 * This will format the value with 2 decimal digits.
 *
 * @param bytes the value that should be formatted
 * @param precision
 */
@Pipe({
  name: 'fileSize'
})
export class FileSizePipe implements PipeTransform {

  /**
   * @param bytes the value that should be formatted
   * @param precision used to determine the number of decimal digits
   * @return formatted value
   */
  transform(bytes = 0, precision = 1): string {
    if (isNaN(parseFloat(String(bytes)))) {
      return '';
    }

    const fileSize = reduceWhile(
      greaterThan1000,
      divideBy1000,
      {size: bytes, unit: BYTES},
      UNITS
    );

    return formatFileSize(fileSize, precision);
  }
}
